import { useState, useCallback } from 'react';
import { FormField, FormData, FormFiles } from '../types/ContactForm';
import {useTranslation} from "react-i18next";

interface UseFormValidationProps {
  formData: FormData;
  files: FormFiles;
  captcha: {
    num1: number;
    num2: number;
    result: string;
  };
  enableCaptcha: boolean;
  captchaType?: 'math' | 'google_recaptcha';
  currentStep: number;
  totalSteps: number;
  isMultiStep: boolean;
  formFields?: FormField[]; 
}

interface ValidationErrors {
  [fieldName: string]: string;
}

export const useFormValidation = ({
  formData,
  files,
  captcha,
  enableCaptcha,
  captchaType = 'math',
  currentStep,
  totalSteps,
  isMultiStep,
  formFields
}: UseFormValidationProps) => {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const { t } = useTranslation('form');

 
  const getAllowedMimeTypes = (allowedFileTypes?: string): string[] => {
    if (!allowedFileTypes || allowedFileTypes.trim() === '') {
      return []; 
    }

    const extensionToMimeMap: { [key: string]: string[] } = {
      '.pdf': ['application/pdf'],
      '.doc': ['application/msword'],
      '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      '.txt': ['text/plain'],
      '.jpg': ['image/jpeg'],
      '.jpeg': ['image/jpeg'],
      '.png': ['image/png'],
      '.gif': ['image/gif'],
      '.bmp': ['image/bmp'],
      '.webp': ['image/webp'],
      '.svg': ['image/svg+xml'],
      '.zip': ['application/zip'],
      '.rar': ['application/x-rar-compressed'],
      '.7z': ['application/x-7z-compressed'],
      '.mp3': ['audio/mpeg'],
      '.wav': ['audio/wav'],
      '.mp4': ['video/mp4'],
      '.avi': ['video/x-msvideo'],
      '.mov': ['video/quicktime'],
      '.xls': ['application/vnd.ms-excel'],
      '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      '.ppt': ['application/vnd.ms-powerpoint'],
      '.pptx': ['application/vnd.openxmlformats-officedocument.presentationml.presentation']
    };

    const extensions = allowedFileTypes
      .split(',')
      .map(ext => ext.trim().toLowerCase())
      .filter(ext => ext)
      .map(ext => ext.startsWith('.') ? ext : `.${ext}`);

    const mimeTypes: string[] = [];
    extensions.forEach(ext => {
      if (extensionToMimeMap[ext]) {
        mimeTypes.push(...extensionToMimeMap[ext]);
      }
    });

    return mimeTypes;
  };
  const clearFieldError = useCallback((fieldName: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  const validateField = useCallback((field: FormField): string | null => {
    const { name, type, required, label } = field;
    const value = formData[name];
    const file = files[name];

    // Handle file fields
    if (type === 'file') {
      if (required && !file) {
        return t('fieldRequired', { fieldName: label });
      }
      
      if (file) {
        const maxFileSize = (field.maxFileSize || 5) * 1024 * 1024; // Convert MB to bytes
        if (file.size > maxFileSize) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          const maxSizeMB = field.maxFileSize || 5;
          return t('fileTooLarge', { maxSize: maxSizeMB, fileSize: fileSizeMB });
        }
        
        
        const allowedTypes = getAllowedMimeTypes(field.allowedFileTypes);
        if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
          const allowedExtensions = field.allowedFileTypes || 'PDF, DOC, DOCX, TXT, JPG, PNG';
          return t('fileTypeNotAllowed', { allowedTypes: allowedExtensions });
        }
      }
      return null;
    }

    // Handle acceptance fields (must be checked when required)
    if (type === 'acceptance') {
      if (required && !value) {
        return t('acceptanceRequired', { fieldName: label });
      }
      return null;
    }

    // Handle star rating fields
    if (type === 'star-rating') {
      if (required && (!value || value === 0)) {
        return t('ratingRequired', { fieldName: label });
      }
      
      if (value && (value < 1 || value > (field.maxStars || 5))) {
        return t('ratingRangeError', { maxStars: field.maxStars || 5 });
      }
      
      return null;
    }

    // Handle other field types
    if (required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      return t('fieldRequired', { fieldName: label });
    }

    if (value && typeof value === 'string') {
      // Email validation
      if (type === 'email' || type === 'email-with-copy') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return t('pleaseEnterValidEmail');
        }
      }

      // URL validation
      if (type === 'url') {
        try {
          new URL(value);
          // Additional check for valid protocol
          if (!value.match(/^https?:\/\/.+/i)) {
            return t('urlMustStartWithProtocol');
          }
        } catch {
          return t('pleaseEnterValidUrl');
        }
      }

      // Phone validation
      if (type === 'tel') {
        const cleanPhone = value.replace(/[^0-9+\-\s()]/g, '');
        if (cleanPhone.length < 5) {
          return t('pleaseEnterValidPhone');
        }
      }

      // Text length validation
      if ((type === 'text' || type === 'url' || type === 'textarea') && value.length > 5000) {
        return t('textTooLong');
      }
    }

    return null;
  }, [formData, files, t]);

  const validateCurrentStep = useCallback((fields: FormField[]): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;


    fields.forEach(field => {
      const error = validateField(field);
      if (error) {
        newErrors[field.name] = error;
        isValid = false;
      }
    });

 
    if (enableCaptcha && (!isMultiStep || currentStep === totalSteps)) {
      if (captchaType === 'google_recaptcha') {
   
      } else {
      
        if (!captcha.result) {
          newErrors.captcha = t('pleaseSolveCaptcha');
          isValid = false;
        } else {
          const expectedResult = captcha.num1 + captcha.num2;
          if (parseInt(captcha.result) !== expectedResult) {
            newErrors.captcha = t('wrongCaptchaAnswer');
            isValid = false;
          }
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  }, [validateField, enableCaptcha, isMultiStep, currentStep, totalSteps, captcha, captchaType, t]);

  const validateForm = useCallback((allFields: FormField[]): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // Validate all visible fields
    allFields.forEach(field => {
      const error = validateField(field);
      if (error) {
        newErrors[field.name] = error;
        isValid = false;
      }
    });


    if (enableCaptcha) {
      if (captchaType === 'google_recaptcha') {
 
      } else {
        // For mathematical captcha
        if (!captcha.result) {
          newErrors.captcha = t('pleaseSolveCaptcha');
          isValid = false;
        } else {
          const expectedResult = captcha.num1 + captcha.num2;
          if (parseInt(captcha.result) !== expectedResult) {
            newErrors.captcha = t('wrongCaptchaAnswer');
            isValid = false;
          }
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  }, [validateField, enableCaptcha, captcha, captchaType, t]);

  const hasFileSizeErrors = useCallback((): boolean => {
    for (const [fieldName, file] of Object.entries(files)) {
      if (file) {
        
        const fieldConfig = formFields?.find(f => f.name === fieldName && f.type === 'file');
        const maxFileSize = (fieldConfig?.maxFileSize || 5) * 1024 * 1024; 
        
        if (file.size > maxFileSize) {
          return true;
        }
      }
    }
    return false;
  }, [files, formFields]);

  return {
    errors,
    clearFieldError,
    validateCurrentStep,
    validateForm,
    hasFileSizeErrors
  };
}; 